import { FC, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowUpTrayIcon } from '@heroicons/react/24/outline';
import useAdminPastPapers, { AdminPastPaper, PastPaperFilters } from '@/api/useAdminPastPapers';
import DataTable from '@/components/functional/DataTable/DataTable';
import DeletePastPaperButton from '@/components/admin/DeletePastPaperButton';
import PastPaperStatusSelect from '@/components/admin/PastPaperStatusSelect';
import PastPaperTitleEditor from '@/components/admin/PastPaperTitleEditor';
import SelectInput from '@/components/functional/SelectInput/SelectInput';
import { ColumnDef, PaginationState } from '@tanstack/react-table';
import Routes from '@/router/Routes';

const SUBJECT_OPTIONS = [
  { value: '', label: 'All Subjects' },
  { value: 'English Language', label: 'English Language' },
  { value: 'English Literature', label: 'English Literature' },
] as const;

const EXAM_BOARD_OPTIONS = [
  { value: '', label: 'All Exam Boards' },
  { value: 'edexcel', label: 'Edexcel' },
] as const;

const PAPER_NUMBER_OPTIONS = [
  { value: '', label: 'All Papers' },
  { value: 'Paper 1', label: 'Paper 1' },
  { value: 'Paper 2', label: 'Paper 2' },
] as const;

const currentYear = new Date().getFullYear();
const MIN_YEAR = 2015;

const YEAR_OPTIONS = [
  { value: '', label: 'All Years' },
  ...Array.from({ length: currentYear - MIN_YEAR + 1 }, (_, i) => ({
    value: String(currentYear - i),
    label: String(currentYear - i),
  })),
] as const;

const SESSION_KEY = 'adminPastPapersState';

const readSession = () => {
  try {
    return JSON.parse(sessionStorage.getItem(SESSION_KEY) || '{}');
  } catch {
    return {};
  }
};

const PastPaperManagement: FC = () => {
  const navigate = useNavigate();
  const session = readSession();

  const [pagination, setPagination] = useState<PaginationState>(
    session.pagination ?? { pageIndex: 0, pageSize: 15 }
  );
  const [filters, setFilters] = useState<PastPaperFilters>(session.filters ?? {});

  useEffect(() => {
    sessionStorage.setItem(SESSION_KEY, JSON.stringify({ pagination, filters }));
  }, [pagination, filters]);

  const { data, isFetching } = useAdminPastPapers({
    pagination,
    filters,
  });

  const handleFilterChange = <K extends keyof PastPaperFilters>(key: K, value: string) => {
    setFilters((prev) => ({
      ...prev,
      [key]: value || undefined,
    }));
    // Reset to first page when filter changes
    setPagination((prev) => ({ ...prev, pageIndex: 0 }));
  };

  const pastPapers = data?.data ?? [];
  const rowCount = data?.rowCount ?? 0;
  const pageCount = Math.ceil(rowCount / pagination.pageSize);

  const formatDate = (dateString: string | null) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GB', {
      month: 'long',
      year: 'numeric',
    });
  };

  const columns: ColumnDef<AdminPastPaper>[] = useMemo(
    () => [
      {
        accessorKey: 'title',
        header: 'Past Paper Name',
        enableSorting: false,
        cell: ({ row }) => (
          <PastPaperTitleEditor pastPaperId={row.original.id} currentTitle={row.original.title} />
        ),
      },
      {
        accessorKey: 'subject',
        header: 'Subject',
        enableSorting: false,
        cell: (info) => <span className="text-gray-600">{info.getValue() as string}</span>,
      },
      {
        accessorKey: 'exam_board',
        header: 'Exam board',
        enableSorting: false,
        cell: (info) => <span className="text-gray-600">{info.getValue() as string}</span>,
      },
      {
        accessorKey: 'paper_number',
        header: 'Paper number',
        enableSorting: false,
        cell: (info) => <span className="text-gray-600">{(info.getValue() as string) || 'N/A'}</span>,
      },
      {
        accessorKey: 'paper_code',
        header: 'Paper Code',
        enableSorting: false,
        cell: (info) => (
          <span className="text-gray-600 font-mono text-sm">
            {(info.getValue() as string) || 'N/A'}
          </span>
        ),
      },
      {
        accessorKey: 'date',
        header: 'Paper Date',
        enableSorting: false,
        cell: (info) => <span className="text-gray-600">{formatDate(info.getValue() as string | null)}</span>,
      },
      {
        accessorKey: 'status',
        header: 'Status',
        enableSorting: false,
        cell: ({ row }) => <PastPaperStatusSelect pastPaper={row.original} />,
      },
      {
        id: 'actions',
        header: 'Actions',
        enableSorting: false,
        cell: ({ row }) => (
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate(Routes.ADMIN_EDIT_PAST_PAPER.replace(':paperId', row.original.id))}
              className="text-gray-900 font-semibold hover:text-gray-700 transition-colors"
            >
              Edit questions
            </button>
            <DeletePastPaperButton paper={row.original} />
          </div>
        ),
      },
    ],
    [navigate]
  );

  return (
    <div className="min-h-full">
      {/* Header */}
      <div className="bg-white rounded-3xl flex justify-between items-center px-8 py-6">
        <h1 className="text-3xl font-semibold text-gray-900">Past Paper Management</h1>
        <button
          onClick={() => navigate(Routes.ADMIN_UPLOAD_PAST_PAPER)}
          className="flex items-center gap-2 px-4 py-2 bg-[#0f172a] text-white rounded-xl hover:bg-[#1e293b] transition-colors"
        >
          <ArrowUpTrayIcon className="w-5 h-5" />
          <span>Upload Paper</span>
        </button>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-3xl px-8 py-6 mt-6">
        <div className="grid grid-cols-4 gap-4">
          <SelectInput
            value={filters.subject || ''}
            onChange={(value) => handleFilterChange('subject', value)}
            options={[...SUBJECT_OPTIONS]}
            label="Subject"
          />
          <SelectInput
            value={filters.examBoard || ''}
            onChange={(value) => handleFilterChange('examBoard', value)}
            options={[...EXAM_BOARD_OPTIONS]}
            label="Exam Board"
          />
          <SelectInput
            value={filters.paperNumber || ''}
            onChange={(value) => handleFilterChange('paperNumber', value)}
            options={[...PAPER_NUMBER_OPTIONS]}
            label="Paper Number"
          />
          <SelectInput
            value={filters.year || ''}
            onChange={(value) => handleFilterChange('year', value)}
            options={[...YEAR_OPTIONS]}
            label="Year"
          />
        </div>
      </div>

      <div className="bg-white rounded-3xl overflow-hidden shadow-sm mt-6">
        {/* Table */}
        <DataTable
          data={pastPapers}
          columns={columns}
          wrapperClassName="bg-white"
          headClassName="bg-gray-50 border-t border-gray-100"
          rowClassName="hover:bg-gray-50 border-b border-gray-100 last:border-b-0"
          showPagination={true}
          manualPagination={true}
          paginationState={pagination}
          onPaginationChange={setPagination}
          pageCount={pageCount}
          rowCount={rowCount}
          loading={isFetching}
        />
      </div>
    </div>
  );
};

export default PastPaperManagement;
